import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useRoles, useDeleteRole, useAssignRole, useRemoveRole, useAvailablePermissions } from '../hooks/useRoles.ts';
import { useAdmins } from '../hooks/useAdmins.ts';
import { useIsAdmin } from '../hooks/useIsAdmin.ts';
import { useGetProfile } from '../hooks/useProfile.ts';
import Modal from '../components/Modal.tsx';
import { 
  Plus, 
  Search, 
  Shield, 
  Users, 
  Edit, 
  Trash2, 
  Eye, 
  Settings,
  UserPlus,
  UserMinus,
  Lock,
  Unlock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import toast from 'react-hot-toast';
import { PermissionPage, PermissionGate } from '../components/PermissionGate';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../utils/permissions';

const RolesPage = () => {
  const { can } = usePermissions();
  const { data: profile } = useGetProfile();
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleteRoleModal, setShowDeleteRoleModal] = useState(false);
  const [showAssignRoleModal, setShowAssignRoleModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [expandedRoles, setExpandedRoles] = useState(new Set());
  const [expandedModules, setExpandedModules] = useState(new Set());

  const queryClient = useQueryClient();

  // API hooks
  const { data: rolesData, isLoading: isLoadingRoles } = useRoles({
    search: searchQuery,
    per_page: 15
  });
  
  const { data: adminsData } = useAdmins({ per_page: 15 });
  const { data: availablePermissions } = useAvailablePermissions();
  const deleteRole = useDeleteRole();
  const assignRole = useAssignRole();
  const removeRole = useRemoveRole();

  const roles = rolesData?.data || [];
  const admins = adminsData?.data || [];

  const handleCreateRole = () => {
    navigate('/roles/create');
  };

  const handleEditRole = (role) => {
    navigate(`/roles/${role.id}/edit`);
  };

  const handleDeleteRole = (role) => {
    setSelectedRole(role);
    setShowDeleteRoleModal(true);
  };

  const handleAssignRole = (role) => {
    setSelectedRole(role);
    setShowAssignRoleModal(true);
  };



  const confirmDeleteRole = async () => {
    try {
      await deleteRole.mutateAsync(selectedRole.id);
      setShowDeleteRoleModal(false);
      toast.success('Role deleted successfully!');
      queryClient.invalidateQueries(['roles']);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete role');
    }
  };



  const toggleRoleExpansion = (roleId) => {
    setExpandedRoles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(roleId)) {
        newSet.delete(roleId);
      } else {
        newSet.add(roleId);
      }
      return newSet;
    });
  };

  const toggleModuleExpansion = (module) => {
    setExpandedModules(prev => {
      const newSet = new Set(prev);
      if (newSet.has(module)) {
        newSet.delete(module);
      } else {
        newSet.add(module);
      }
      return newSet;
    });
  };

  const groupedPermissions = availablePermissions?.data || {};

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Lock className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Access Denied</h3>
          <p className="mt-1 text-sm text-gray-500">
            You don't have permission to access role management.
          </p>
        </div>
      </div>
    );
  }

  const userRole = profile?.data?.current_role || profile?.data?.role?.name || profile?.data?.user?.role?.name;
  const isVillageAdmin = userRole === 'village_admin' && !isAdmin;

  if (isVillageAdmin) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Shield className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Access Denied</h3>
          <p className="mt-1 text-sm text-gray-500">
            Village Admins cannot access roles or permissions management.
          </p>
        </div>
      </div>
    );
  }

  return (
    <PermissionPage permission={PERMISSIONS.VIEW_ROLES}>
    <div className="min-h-screen bg-gray-100 dark:bg-slate-900">
      <div className="container mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 dark:text-white">Role Management</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">Manage roles and permissions for your organization</p>
          </div>
          <PermissionGate permission={PERMISSIONS.CREATE_ROLE}>
          <button
            onClick={handleCreateRole}
            className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 flex items-center gap-2"
          >
            <Plus size={20} />
            Create Role
          </button>
          </PermissionGate>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 kpi-grid">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Shield className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Roles</p>
                <p className="text-2xl font-bold text-gray-900">{roles.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active Roles</p>
                <p className="text-2xl font-bold text-gray-900">
                  {roles.filter(role => role.is_active).length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <Settings className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Permissions Available</p>
                <p className="text-2xl font-bold text-gray-900">
                  {Object.values(groupedPermissions).flat().length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search roles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>
        </div>

        {/* Roles List */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Roles</h2>
            
            {isLoadingRoles ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                <p className="text-gray-500 dark:text-gray-400 mt-2">Loading roles...</p>
              </div>
            ) : roles.length === 0 ? (
              <div className="text-center py-8">
                <Shield className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No roles found</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Get started by creating your first role.
                </p>
                <div className="mt-6">
                  <button
                    onClick={handleCreateRole}
                    className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700"
                  >
                    Create Role
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {roles.map((role) => (
                  <div key={role.id} className="border border-gray-200 dark:border-slate-700 rounded-lg">
                    <div className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => toggleRoleExpansion(role.id)}
                            className="p-1 rounded hover:bg-gray-100"
                          >
                            {expandedRoles.has(role.id) ? (
                              <ChevronDown size={16} />
                            ) : (
                              <ChevronRight size={16} />
                            )}
                          </button>
                          <div>
                            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                              {role.display_name}
                              {role.is_active ? (
                                <CheckCircle size={16} className="text-green-500" />
                              ) : (
                                <XCircle size={16} className="text-red-500" />
                              )}
                            </h3>
                            <p className="text-sm text-gray-500">{role.description}</p>
                            <p className="text-xs text-gray-400">
                              {role.permissions?.length || 0} permissions assigned
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleAssignRole(role)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                            title="Assign to Users"
                          >
                            <UserPlus size={16} />
                          </button>
                          {can(PERMISSIONS.EDIT_ROLE) && (
                          <button
                            onClick={() => handleEditRole(role)}
                            className="p-2 text-gray-600 hover:bg-gray-50 rounded"
                            title="Edit Role"
                          >
                            <Edit size={16} />
                          </button>
                          )}
                          {can(PERMISSIONS.DELETE_ROLE) && (
                          <button
                            onClick={() => handleDeleteRole(role)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                            title="Delete Role"
                          >
                            <Trash2 size={16} />
                          </button>
                          )}
                        </div>
                      </div>

                      {/* Expanded Role Details */}
                      {expandedRoles.has(role.id) && (
                        <div className="mt-4 pl-6 border-l-2 border-gray-200">
                          <h4 className="font-medium text-gray-800 mb-2">Permissions</h4>
                          {role.permissions && role.permissions.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                              {role.permissions.map((permission) => (
                                <div
                                  key={permission.id}
                                  className="bg-gray-50 px-3 py-1 rounded text-sm text-gray-700"
                                >
                                  {permission.display_name}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500">No permissions assigned</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>





      {/* Delete Role Modal */}
      <Modal
        isOpen={showDeleteRoleModal}
        onClose={() => setShowDeleteRoleModal(false)}
        title="Delete Role"
      >
        <div className="space-y-4">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="h-6 w-6 text-red-600" />
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                Delete {selectedRole?.display_name}?
              </h3>
              <p className="text-sm text-gray-500">
                This action cannot be undone. The role will be permanently removed.
              </p>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              onClick={() => setShowDeleteRoleModal(false)}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={confirmDeleteRole}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
            >
              Delete Role
            </button>
          </div>
        </div>
      </Modal>

      {/* Assign Role Modal */}
      <Modal
        isOpen={showAssignRoleModal}
        onClose={() => setShowAssignRoleModal(false)}
        title={`Assign ${selectedRole?.display_name} Role`}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Select users to assign the {selectedRole?.display_name} role to:
          </p>

          <div className="max-h-64 overflow-y-auto">
            {admins.map((admin) => (
              <div key={admin.id} className="flex items-center justify-between p-3 border-b border-gray-100">
                <div>
                  <p className="font-medium text-gray-900">{admin.name}</p>
                  <p className="text-sm text-gray-500">{admin.phone_number}</p>
                  {admin.role_in_group && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        {admin.role_in_group.display_name}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      // Check if user already has this role
                      const hasRole = admin.role_in_group?.name === selectedRole?.name;
                      if (hasRole) {
                        // Remove role
                        removeRole.mutateAsync({
                          user_id: admin.id
                        }).then(() => {
                          toast.success('Role removed successfully!');
                          queryClient.invalidateQueries(['admins']);
                        }).catch((error) => {
                          toast.error(error.response?.data?.message || 'Failed to remove role');
                        });
                      } else {
                        // Assign role (replaces any existing role)
                        assignRole.mutateAsync({
                          user_id: admin.id,
                          role_id: selectedRole.id
                        }).then(() => {
                          toast.success('Role assigned successfully!');
                          queryClient.invalidateQueries(['admins']);
                        }).catch((error) => {
                          toast.error(error.response?.data?.message || 'Failed to assign role');
                        });
                      }
                    }}
                    className={`px-3 py-1 rounded text-sm ${
                      admin.role_in_group?.name === selectedRole?.name
                        ? 'bg-red-100 text-red-700 hover:bg-red-200'
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                  >
                    {admin.role_in_group?.name === selectedRole?.name ? (
                      <>
                        <UserMinus size={14} className="inline mr-1" />
                        Remove
                      </>
                    ) : (
                      <>
                        <UserPlus size={14} className="inline mr-1" />
                        {admin.role_in_group ? 'Replace' : 'Assign'}
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end pt-4">
            <button
              onClick={() => setShowAssignRoleModal(false)}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
    </div>
    </PermissionPage>
  );
};

export default RolesPage; 